<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type Plotly from "plotly.js-dist-min"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useConfigStore } from "@core/stores/config.store"
import { useAlarmeStore } from "@modules/energy-manager/stores/alarme.store"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"
import {
    groupAlarmThresholdVariableOptions,
    PROFIL_TO_THRESHOLD_FREQ,
    type AlarmThresholdVariableOption,
} from "@modules/energy-manager/utils/alarmThresholdVariablesOptions"
import { createEmptyThresholdPoints } from "@modules/energy-manager/utils/thresholdPoints"
import {
    ALARM_THRESHOLD_CHART_COLORS,
    ALARM_THRESHOLD_PLOTLY_MARKER,
} from "@modules/energy-manager/constants/alarme"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        type: "building" | "source"
        itemId: string
        variablesOptions: AlarmThresholdVariableOption[]
        loading?: boolean
    }>(),
    {
        loading: false,
    }
)

const emit = defineEmits<{ cancel: []; save: [] }>()
const alarmeStore = useAlarmeStore()
const configStore = useConfigStore()
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                step1Title:
                    "1. Quelles sont les données que vous voulez surveiller",
                step2Title: "2. À quelle fréquence voulez vous regarder",
                step3Title: "3. Construisez votre profil",
                variablePlaceholder: "Sélectionner une variable",
                freqHourly: "Horaire",
                freqDaily: "Quotidien",
                seriesConsigne: "Consigne",
                seriesLimHaute: "Limite haute",
                seriesLimBasse: "Limite basse",
                saveSuccess: "Le profil a bien été sauvegardé.",
                saveError: "Une erreur est survenue lors de la sauvegarde.",
                save: "Sauvegarder",
                cancel: "Annuler",
                variableGroup: {
                    energy_power: "Énergie, puissance et coûts",
                    indoor_air: "Confort et qualité de l'air",
                    hydraulic: "Températures hydrauliques et chauffage",
                    contacts: "Contacts et équipements",
                    grid_signal: "Réseau et signalisation",
                    other: "Autres",
                },
            },
        },
    },
})

// ================================
// STATE
// ================================
const form = ref<{
    variableSelectedForm: string
    typedFreq: "hourly" | "daily" | ""
}>({
    variableSelectedForm: "",
    typedFreq: "",
})
const profile = ref<{
    points: ThresholdPoint[]
    freq: "hourly" | "daily"
} | null>(null)
const points = ref<ThresholdPoint[]>([])
/** Ref sur le formulaire seuils (validation vee-validate : ordre limite basse / consigne / limite haute). */
const alarmThresholdFormRef = ref<{ isValidThresholdForm: boolean } | null>(
    null
)
const {
    fetch: persistNewThreshold,
    isFetching: isSavingThreshold,
    error: saveThresholdError,
    hasError: saveThresholdHasError,
    isSuccess: saveThresholdSucceeded,
} = useFetchWrapper(async () => {
    await alarmeStore.addThreshold(
        { scope: props.type, id: props.itemId },
        form.value.variableSelectedForm,
        points.value
    )
})

const saveFeedbackMessage = computed(() => {
    if (saveThresholdHasError.value && saveThresholdError.value) {
        return saveThresholdError.value instanceof ServiceError
            ? saveThresholdError.value.message
            : t("alarm.saveError")
    }
    if (saveThresholdSucceeded.value) {
        return t("alarm.saveSuccess")
    }
    return ""
})

// ================================
// COMPUTED
// ================================
const getConfigVariableForForm = computed(() => {
    if (!form.value.variableSelectedForm) return null
    return (
        configStore.getVariableByType(form.value.variableSelectedForm) ?? null
    )
})

/** Unité d’affichage sur l’axe Y du graphique (config variable). */
const getChartYAxisUnit = computed(
    () => getConfigVariableForForm.value?.unit ?? ""
)

/**
 * Fréquences proposées pour la variable courante (profils config), comme ProfileFormAdd legacy.
 */
const getFreqOptions = computed(() => {
    const variable = getConfigVariableForForm.value
    const profils = variable?.profils ?? []
    const options: Array<{ label: string; value: "hourly" | "daily" }> = []
    const seen = new Set<"hourly" | "daily">()
    for (const p of profils) {
        const freq = PROFIL_TO_THRESHOLD_FREQ[p]
        if (!freq || seen.has(freq)) continue
        seen.add(freq)
        options.push({
            label:
                freq === "hourly"
                    ? t("alarm.freqHourly")
                    : t("alarm.freqDaily"),
            value: freq,
        })
    }
    return options
})

const getValidForm = computed(
    () =>
        Boolean(form.value.variableSelectedForm) &&
        Boolean(form.value.typedFreq)
)

const getChartData = computed<Plotly.Data[]>(() => {
    const xs = points.value.map((point) => point.timestamp)
    return [
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesConsigne"),
            x: xs,
            y: points.value.map((point) => point.consigne),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.consigne },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.consigne,
            },
        },
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesLimHaute"),
            x: xs,
            y: points.value.map((point) => point.limhaute),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.limhaute },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.limhaute,
            },
        },
        {
            type: "scatter" as const,
            mode: "lines+markers" as const,
            name: t("alarm.seriesLimBasse"),
            x: xs,
            y: points.value.map((point) => point.limbasse),
            line: { color: ALARM_THRESHOLD_CHART_COLORS.limbasse },
            marker: {
                ...ALARM_THRESHOLD_PLOTLY_MARKER,
                color: ALARM_THRESHOLD_CHART_COLORS.limbasse,
            },
        },
    ]
})

const getCanSave = computed(
    () =>
        getValidForm.value &&
        Boolean(profile.value) &&
        points.value.some(
            (point) =>
                point.consigne !== 0 ||
                point.limhaute !== 0 ||
                point.limbasse !== 0
        ) &&
        alarmThresholdFormRef.value?.isValidThresholdForm === true
)

const getHasErrorAlert = computed(() => saveThresholdHasError.value)
const getHasSuccessAlert = computed(() => saveThresholdSucceeded.value)

/**
 * Option sélectionnée (icône / couleur) pour l’affichage du Select fermé.
 */
const getSelectedVariableOption = computed(
    (): AlarmThresholdVariableOption | null => {
        const id = form.value.variableSelectedForm
        if (!id) return null
        return props.variablesOptions.find((o) => o.value === id) ?? null
    }
)

/**
 * Variables regroupées par famille pour le Select (PrimeVue groupes).
 */
const getGroupedVariableOptions = computed(() =>
    groupAlarmThresholdVariableOptions(props.variablesOptions, (id) =>
        t(`alarm.variableGroup.${id}`)
    )
)

// ================================
// INTERACTIONS
// ================================
function onSelectFrequency(freq: "hourly" | "daily") {
    form.value.typedFreq = freq
    const length = freq === "hourly" ? 168 : 7
    points.value = createEmptyThresholdPoints(length)
}

/**
 * Réinitialise variable / fréquence / points lorsque la sélection est vidée.
 *
 * @returns void
 */
function clearVariableSelectionState() {
    form.value.variableSelectedForm = ""
    form.value.typedFreq = ""
    profile.value = null
    points.value = []
}

/**
 * Met à jour la variable (id métier) et initialise la fréquence après sélection dans le Select.
 *
 * @param value identifiant de variable ou valeur vide (effacement)
 * @returns void
 */
function onVariableSelectChange(value: string | null | undefined) {
    if (value == null || value === "") {
        clearVariableSelectionState()
        return
    }

    form.value.variableSelectedForm = value

    const freqs = getFreqOptions.value
    if (freqs.length === 0) {
        form.value.typedFreq = ""
        profile.value = null
        points.value = []
        return
    }
    if (freqs.length === 1) {
        onSelectFrequency(freqs[0].value)
        return
    }
    onSelectFrequency("hourly")
}

watch(
    () => props.variablesOptions,
    (options) => {
        const id = form.value.variableSelectedForm
        if (!id) {
            return
        }
        if (options.length === 0) {
            return
        }
        const found = options.find((o) => o.value === id) ?? null
        if (!found) {
            clearVariableSelectionState()
        }
    },
    { deep: true }
)

/**
 * Reconstruit le profil lors des changements d'étape.
 * @returns void
 */
function onBuildProfile() {
    if (!form.value.typedFreq || !form.value.variableSelectedForm) {
        profile.value = null
        return
    }
    profile.value = {
        points: [...points.value],
        freq: form.value.typedFreq,
    }
}

async function onSave() {
    if (!getCanSave.value) return
    await persistNewThreshold()
    if (!saveThresholdHasError.value) {
        emit("save")
    }
}

watch(
    () => ({ ...form.value }),
    (nextForm) => {
        if (nextForm.variableSelectedForm && nextForm.typedFreq) {
            onBuildProfile()
        }
    },
    { deep: true }
)

watch(
    points,
    () => {
        if (form.value.variableSelectedForm && form.value.typedFreq) {
            onBuildProfile()
        }
    },
    { deep: true }
)
</script>

<template>
    <div class="flex flex-col gap-6 rounded-xl bg-white p-4">
        <div class="h-1" />

        <div class="flex flex-col gap-6">
            <div id="select-variable" class="flex flex-col gap-2">
                <h4 class="p-4 text-center text-xl font-semibold text-primary">
                    {{ t("alarm.step1Title") }}
                </h4>
                <BaseSelect
                    class="w-full"
                    input-id="alarm-threshold-variable-select"
                    :model-value="form.variableSelectedForm"
                    :options="getGroupedVariableOptions"
                    :placeholder="t('alarm.variablePlaceholder')"
                    option-label="name"
                    option-value="value"
                    option-group-label="label"
                    option-group-children="items"
                    filter
                    show-clear
                    reset-filter-on-hide
                    @update:model-value="onVariableSelectChange"
                >
                    <template #value="{ placeholder }">
                        <div
                            v-if="getSelectedVariableOption"
                            class="flex min-h-9 w-full items-center gap-2.5 py-0.5"
                        >
                            <span
                                class="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 ring-1 ring-inset ring-slate-200/70"
                            >
                                <span
                                    v-if="getSelectedVariableOption.color"
                                    class="pointer-events-none absolute inset-0 opacity-[0.18]"
                                    :style="{
                                        backgroundColor: String(
                                            getSelectedVariableOption.color
                                        ),
                                    }"
                                />

                                <BaseIcon
                                    v-if="getSelectedVariableOption.icon"
                                    class="relative z-[1]"
                                    :icon="
                                        String(getSelectedVariableOption.icon)
                                    "
                                    :color="
                                        getSelectedVariableOption.color ||
                                        '#334155'
                                    "
                                    size="1.125rem"
                                />
                                <i
                                    v-else
                                    class="pi pi-chart-line relative z-[1] text-[1.05rem] text-slate-500"
                                    aria-hidden="true"
                                />
                            </span>
                            <span
                                class="min-w-0 flex-1 truncate text-left text-slate-900"
                            >
                                {{ getSelectedVariableOption.name }}
                            </span>
                        </div>
                        <span v-else class="text-slate-400">{{
                            placeholder
                        }}</span>
                    </template>
                    <template #optiongroup="slotProps">
                        <span
                            class="text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                            {{ slotProps.option.label }}
                        </span>
                    </template>
                    <template #option="slotProps">
                        <div class="flex min-h-9 items-center gap-2.5 py-0.5">
                            <span
                                class="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50 ring-1 ring-inset ring-slate-200/70"
                            >
                                <span
                                    v-if="slotProps.option.color"
                                    class="pointer-events-none absolute inset-0 opacity-[0.18]"
                                    :style="{
                                        backgroundColor: String(
                                            slotProps.option.color
                                        ),
                                    }"
                                />

                                <BaseIcon
                                    v-if="slotProps.option.icon"
                                    class="relative z-[1]"
                                    :icon="String(slotProps.option.icon)"
                                    :color="slotProps.option.color || '#334155'"
                                    size="1.125rem"
                                />
                                <i
                                    v-else
                                    class="pi pi-chart-line relative z-[1] text-[1.05rem] text-slate-500"
                                    aria-hidden="true"
                                />
                            </span>
                            <span
                                class="min-w-0 flex-1 truncate text-slate-900"
                            >
                                {{ slotProps.option.name }}
                            </span>
                        </div>
                    </template>
                </BaseSelect>
            </div>

            <div v-if="form.variableSelectedForm" class="flex flex-col gap-6">
                <div id="select-freq" class="flex flex-col gap-2">
                    <h4
                        class="p-4 text-center text-xl font-semibold text-primary"
                    >
                        {{ t("alarm.step2Title") }}
                    </h4>
                    <div class="flex flex-wrap justify-center gap-2">
                        <BaseButton
                            v-for="(freq, index) in getFreqOptions"
                            :key="`freq-btn-${freq.value}-${index}`"
                            :outlined="freq.value !== form.typedFreq"
                            :severity="
                                freq.value === form.typedFreq
                                    ? 'primary'
                                    : 'secondary'
                            "
                            :label="freq.label"
                            @click="onSelectFrequency(freq.value)"
                        />
                    </div>
                </div>
                <h4
                    class="p-4 pb-0 text-center text-xl font-semibold text-primary"
                >
                    {{ t("alarm.step3Title") }}
                </h4>
                <BaseCard
                    v-if="getValidForm"
                    id="form-profile"
                    class="flex flex-col gap-4"
                >
                    <template #content>
                        <div class="flex flex-col gap-4">
                            <AlarmForm
                                ref="alarmThresholdFormRef"
                                :points="points"
                                :variable-unit="getChartYAxisUnit"
                                @change-points="points = $event"
                            />
                            <AlarmPlotlyChart
                                v-if="getValidForm && profile"
                                :chart-data="getChartData"
                                :y-axis-unit="getChartYAxisUnit"
                                :frequency="profile.freq"
                                :disable-zoom="true"
                            />
                        </div>
                    </template>
                </BaseCard>
            </div>
        </div>

        <div class="flex flex-wrap justify-center gap-4">
            <BaseButton
                :disabled="!getCanSave || props.loading"
                :loading="isSavingThreshold"
                :label="t('alarm.save')"
                @click="onSave"
            />
            <BaseButton
                outlined
                severity="secondary"
                :disabled="props.loading"
                :label="t('alarm.cancel')"
                @click="emit('cancel')"
            />
        </div>

        <div
            v-if="getHasErrorAlert || getHasSuccessAlert"
            class="rounded-md px-3 py-2 text-sm"
            :class="
                getHasErrorAlert
                    ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200'
                    : 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
            "
        >
            {{ saveFeedbackMessage }}
        </div>
    </div>
</template>
